import { Grid2 } from "@mui/material";
import { CheckIcon, RupeeIcon } from "components";
import GKButton from "components/GKButton";
import GKDatePicker from "components/GKDatePicker";
import GKModal from "components/GKModal";
import GKTextField from "components/GKTextField";
import dayjs from "dayjs";
import { useMutation } from "hooks/useQueries";
import { Controller, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { refetchQuery } from "utils/helpers";

interface Props {
  portfolioId: string;
  highWatermarkData: any;
}

const HighWaterMarkFormModal = (props: Props & GKModalProps) => {
  const { open, setOpen, portfolioId, highWatermarkData } = props;

  const form = useForm({
    defaultValues: {
      highWatermark: highWatermarkData?.highWatermark || "",
      date: highWatermarkData?.date || "",
    },
  });

  const createHighWaterMark = useMutation({
    mutationKey: ["ADD_HIGH_WATERMARK"],
    mutationFn: (values: { highWatermark: string; date: string }) =>
      axiosInstance.post(
        `/api/revenue/high-watermark/?portfolio=${portfolioId}`,
        values,
      ),
    onSuccess: () => {
      setOpen(false);
      refetchQuery("GET_HIGH_WATERMARK_HISTORY_BY_PORTFOLIO_ID");
    },
  });

  const updateHighWatermark = useMutation({
    mutationKey: ["UPDATE_HIGH_WATERMARK"],
    mutationFn: (values: { highWatermark: string; date: string }) =>
      axiosInstance.patch(
        `/api/revenue/high-watermark/${highWatermarkData.id}/?portfolio=${portfolioId}`,
        values,
      ),
    onSuccess: () => {
      setOpen(false);
      refetchQuery("GET_HIGH_WATERMARK_HISTORY_BY_PORTFOLIO_ID");
    },
  });

  return (
    <GKModal
      modalTitle={"Add High Watermark"}
      open={open}
      setOpen={setOpen}
      width={"273px"}
      footer={
        <GKButton
          fullWidth
          variant="contained"
          type="submit"
          form="high-watermark-form"
          startIcon={<CheckIcon />}
          loading={
            updateHighWatermark?.isLoading || createHighWaterMark?.isLoading
          }
        >
          Add High Watermark
        </GKButton>
      }
    >
      <form
        id="high-watermark-form"
        onSubmit={form.handleSubmit((values) =>
          highWatermarkData.id
            ? updateHighWatermark.mutate(values)
            : createHighWaterMark.mutate(values),
        )}
      >
        <Grid2 container spacing={2} p={"16px 0"}>
          <Grid2 size={12}>
            <Controller
              name="date"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select a date",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKDatePicker
                  {...field}
                  inputLabel="Date"
                  value={dayjs(field.value)}
                  onChange={(value) =>
                    field.onChange(dayjs(value).format("YYYY-MM-DD"))
                  }
                  disableFuture
                  slotProps={{
                    textField: {
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="highWatermark"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter high watermark amount",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel={"High Watermark Amount"}
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default HighWaterMarkFormModal;
